<script setup>
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon';
import { saveAs } from 'file-saver';
import { PrimaryButton, SecondaryButton, DateRangePicker, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useMeteringPointMembershipsStore } from "@/stores/meteringPointMembershipsStore";
import edaTools from "@/lib/es-bill-shared-js/misc/edaTools";
import { fetchConsumptionDataExport, deleteConsumptionData } from "@/services/energyDataAdminService";

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna';

const communitiesStore = useCommunitiesStore();
const mpmStore = useMeteringPointMembershipsStore();
const snackbarStore = useSnackbarStore();

const communityAvailable = computed(() => communitiesStore.allCommunities.map(c => ({
  title: c.name,
  props: { subtitle: c.ecid },
  ecn: c.ecn,
  ecid: c.ecid,
})));

const selectedCommunity = ref(null);
const selectedMeteringPoint = ref(null);
const dateRange = ref([]);
const loading = ref(false);
const lastExport = ref(null);
const deleteDialog = ref(false);
const deleting = ref(false);
const lastDeleted = ref(null);

const meteringPointsAvailable = computed(() => {
  if (!selectedCommunity.value) return [];
  return mpmStore.allMeteringPointMemberships
    .filter(mpm => mpm.ecn === selectedCommunity.value.ecn
      && mpm.ecid === selectedCommunity.value.ecid)
    .map(mpm => ({
      title: edaTools.shortenIdentifiersLong(mpm.meteringPoint),
      props: { subtitle: `${mpm.nameFriendly || ''} (${mpm.membershipStatus})` },
      meteringPoint: mpm.meteringPoint,
    }));
});

const isoRange = computed(() => {
  if (!dateRange.value || dateRange.value.length === 0) return null;
  const days = dateRange.value
    .map(d => DateTime.fromJSDate(new Date(d)).toISODate())
    .sort();
  return { from: days[0], to: days[days.length - 1] };
});

const requestReady = computed(() =>
  selectedCommunity.value && selectedMeteringPoint.value && isoRange.value);

async function downloadExport() {
  loading.value = true;
  lastExport.value = null;
  try {
    const data = await fetchConsumptionDataExport(
      selectedCommunity.value.ecn,
      selectedCommunity.value.ecid,
      selectedMeteringPoint.value.meteringPoint,
      isoRange.value.from,
      isoRange.value.to,
    );
    lastExport.value = {
      count: data.count,
      ecn: selectedCommunity.value.ecn,
      ecid: selectedCommunity.value.ecid,
      meteringPoint: selectedMeteringPoint.value.meteringPoint,
      from: isoRange.value.from,
      to: isoRange.value.to,
    };
    lastDeleted.value = null;

    const fileName = [
      'consumptionData',
      selectedCommunity.value.ecn,
      selectedMeteringPoint.value.meteringPoint,
      isoRange.value.from,
      isoRange.value.to,
    ].join('_') + '.json';
    saveAs(new Blob([JSON.stringify(data, null, 2)],
      { type: 'application/json;charset=utf-8' }), fileName);
  } catch (error) {
    $log.error('energy data cleanup export failed', error);
    snackbarStore.showSnackbar(t('energyDataCleanup.exportFailed'), '', 'Error', 15000, []);
  } finally {
    loading.value = false;
  }
}

async function confirmDelete() {
  deleting.value = true;
  try {
    const result = await deleteConsumptionData(
      lastExport.value.ecn, lastExport.value.ecid, lastExport.value.meteringPoint,
      lastExport.value.from, lastExport.value.to, lastExport.value.count);
    lastDeleted.value = result.deletedCount;
    deleteDialog.value = false;
    snackbarStore.showSnackbar(
      t('energyDataCleanup.deleteSuccess', { count: result.deletedCount }), '', 'Success', 8000, []);
    lastExport.value = null;
  } catch (error) {
    $log.error('energy data cleanup delete failed', error);
    const detail = error?.response?.status === 409
      ? t('energyDataCleanup.deleteConflict') : '';
    snackbarStore.showSnackbar(t('energyDataCleanup.deleteFailed'), detail, 'Error', 15000, []);
    deleteDialog.value = false;
  } finally {
    deleting.value = false;
  }
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <p class="textHeadline">{{ t('energyDataCleanup.title') }}</p>
      <p class="mb-4">{{ t('energyDataCleanup.description') }}</p>

      <v-combobox
        max-width="428px"
        bg-color="white"
        :placeholder="t('community')"
        v-model="selectedCommunity"
        :items="communityAvailable"
        :filter-keys="['title', 'value']"
        density="comfortable"
        variant="outlined"
        item-value="ecid"
        hide-details
        clearable
        class="mb-4"
      />

      <v-combobox
        max-width="428px"
        bg-color="white"
        :placeholder="t('meteringPointNumber')"
        v-model="selectedMeteringPoint"
        :items="meteringPointsAvailable"
        :filter-keys="['title']"
        density="comfortable"
        variant="outlined"
        item-value="meteringPoint"
        hide-details
        clearable
        :disabled="!selectedCommunity"
        class="mb-4"
      />

      <DateRangePicker
        class="mb-4"
        style="max-width: 428px"
        :dateRange="dateRange"
        :placeholder="t('dateRange')"
        @update:dateRange="(val) => dateRange = val"
      />

      <p v-if="isoRange" class="mb-4">
        {{ t('dateRange') }}: {{ isoRange.from }} — {{ isoRange.to }}
      </p>

      <div class="d-flex ga-4 mb-4">
        <PrimaryButton
          :disabled="!requestReady || loading"
          :loading="loading"
          @click="downloadExport"
          :text="t('energyDataCleanup.downloadJson')"
        />
        <SecondaryButton
          :disabled="!lastExport"
          :text="t('energyDataCleanup.deleteButton')"
          :title="lastExport ? '' : t('energyDataCleanup.deletePending')"
          @click="deleteDialog = true"
        />
      </div>

      <v-alert v-if="lastExport" type="info" variant="tonal" max-width="428px">
        {{ t('energyDataCleanup.exportResult', { count: lastExport.count }) }}
      </v-alert>
      <v-alert v-if="lastDeleted !== null" type="success" variant="tonal" max-width="428px" class="mt-2">
        {{ t('energyDataCleanup.deleteSuccess', { count: lastDeleted }) }}
      </v-alert>

      <v-dialog v-model="deleteDialog" persistent max-width="460">
        <v-card class="pa-6">
          <v-card-title>{{ t('energyDataCleanup.deleteButton') }}</v-card-title>
          <v-card-text>
            <p class="mb-2 font-weight-medium">
              {{ lastExport?.meteringPoint }} — {{ lastExport?.from }} / {{ lastExport?.to }}
            </p>
            <p>{{ t('energyDataCleanup.deleteConfirmText', { count: lastExport?.count }) }}</p>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="deleteDialog = false">{{ t('cancel') }}</v-btn>
            <v-btn color="error" :loading="deleting" @click="confirmDelete">{{ t('confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-container>
</template>
